import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Grid, 
  Paper, 
  Typography, 
  Card, 
  CardContent,
  LinearProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Button,
  Container,
  Stack,
  Divider,
  Avatar
} from '@mui/material';
import {
  ShoppingCart,
  People,
  Inventory,
  Receipt,
  TrendingUp,
  Warning,
  AttachMoney,
  Description,
  ShoppingBag,
  LocalShipping,
  Store,
  CalendarToday,
  ArrowUpward,
  ArrowDownward,
  Visibility,
  Add,
  Notifications,
  Search,
  MoreVert
} from '@mui/icons-material';
import { getDashboardStats, getProducts, getCustomers } from '../../services/api';
import { useNavigate } from 'react-router-dom';

// ADD THIS LINE - Import the ClearCacheButton component
import ClearCacheButton from '../../components/ClearCacheButton';

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch dashboard stats
      const dashboardRes = await getDashboardStats();
      
      if (dashboardRes.success) {
        setStats(dashboardRes.data);
      } else {
        setError(dashboardRes.message || 'Failed to load dashboard statistics');
      }
      
      // Fetch products and customers separately
      try {
        const productsRes = await getProducts();
        if (productsRes.success) {
          setProducts(productsRes.data || []);
        }
      } catch (productError) {
        console.error('Error fetching products:', productError);
        // Continue without products
      }
      
      try {
        const customersRes = await getCustomers();
        if (customersRes.success) {
          setCustomers(customersRes.data || []);
        }
      } catch (customerError) {
        console.error('Error fetching customers:', customerError);
        // Continue without customers
      }
      
    } catch (err) {
      console.error('Dashboard fetch error:', err);
      setError(err.message || 'Failed to load dashboard data. Please check backend connection.');
    } finally {
      setLoading(false);
    }
  };

  const handleViewProduct = (id) => {
    navigate(`/products/${id}`);
  };

  const handleViewCustomer = (id) => {
    navigate(`/customers/${id}`);
  };

  // Format currency helper
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return 'LKR 0.00';
    
    // Check if already formatted
    if (typeof amount === 'string' && amount.includes('LKR')) {
      return amount;
    }
    
    return new Intl.NumberFormat('en-LK', {
      style: 'currency',
      currency: 'LKR',
      minimumFractionDigits: 2
    }).format(parseFloat(amount) || 0);
  };

  if (loading) {
    return (
      <Box sx={{ width: '100%', p: 3 }}>
        <LinearProgress />
        <Typography sx={{ mt: 2, textAlign: 'center' }}>Loading dashboard...</Typography>
      </Box>
    );
  }

  if (error && !stats) {
    return (
      <Container maxWidth="xl" sx={{ py: 4, px: 3 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button 
          variant="contained" 
          onClick={fetchDashboardData}
          sx={{ mt: 2 }}
        >
          Retry
        </Button>
      </Container>
    );
  }

  // Calculate additional stats from products and customers
  const totalInventoryValue = Array.isArray(products) ? products.reduce((sum, product) => 
    sum + (parseFloat(product.price || 0) * parseFloat(product.quantity || 0)), 0
  ) : 0;
  
  const totalOutstanding = Array.isArray(customers) ? customers.reduce((sum, customer) => 
    sum + parseFloat(customer.outstanding_balance || 0), 0
  ) : 0;
  
  const lowStockProducts = Array.isArray(products) ? products.filter(product => 
    parseFloat(product.quantity || 0) <= parseFloat(product.reorder_level || 0)
  ).length : 0;

  // Dashboard stat cards - Use stats from API first, fallback to calculated values
 const statCards = [
  {
    title: 'Total Products',
    value: stats?.totalProducts || (Array.isArray(products) ? products.length : 0),
    icon: <Inventory />,
    color: '#FFFFFF',
    bgColor: '#667eea', // Purple
    iconColor: '#FFFFFF',
    link: '/products'
  },
  {
    title: 'Total Customers',
    value: stats?.totalCustomers || (Array.isArray(customers) ? customers.length : 0),
    icon: <People />,
    color: '#FFFFFF',
    bgColor: '#10b981', // Emerald Green
    iconColor: '#FFFFFF',
    link: '/customers'
  },
 
  //{
   // title: 'Inventory Value',
   // value: stats?.inventoryValue || formatCurrency(totalInventoryValue),
   // icon: <Store />,
   // color: '#FFFFFF',
   // bgColor: '#f59e0b', // Amber
   // iconColor: '#FFFFFF',
   // link: '/inventory'
 // }, 

//  {
  //  title: 'Outstanding Balance',
  //  value: stats?.totalOutstanding || formatCurrency(totalOutstanding),
  //  icon: <AttachMoney />,
  //  color: '#FFFFFF',
 //   bgColor: '#ef4444', // Red
  //  iconColor: '#FFFFFF',
  //  link: '/invoices'
 // }, 
  
  {
    title: 'Low Stock Items',
    value: stats?.lowStockProducts || lowStockProducts,
    icon: <Warning />,
    color: '#FFFFFF',
    bgColor: '#fbbf24', // Yellow
    iconColor: '#FFFFFF',
    link: '/inventory'
  },
  {
    title: 'Active Customers',
    value: stats?.activeCustomers || (Array.isArray(customers) ? customers.filter(c => c.is_active).length : 0),
    icon: <TrendingUp />,
    color: '#FFFFFF',
    bgColor: '#3b82f6', // Blue
    iconColor: '#FFFFFF',
    link: '/customers'
  },
 // {
   // title: 'Total Invoices',
   // value: stats?.totalInvoices || '0',
   // icon: <Receipt />,
   // color: '#FFFFFF',
   // bgColor: '#8b5cf6', // Violet
   // iconColor: '#FFFFFF',
   // link: '/invoices'
 // },
 // {
   // title: 'Invoice Amount',
   // value: stats?.totalInvoiceAmount || formatCurrency(0),
   // icon: <ShoppingCart />,
   // color: '#FFFFFF',
   // bgColor: '#06b6d4', // Cyan
   // iconColor: '#FFFFFF',
   // link: '/invoices'
 // } 
];

  return (
    <Container maxWidth="xl" sx={{ py: 3, px: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 4,
        flexDirection: { xs: 'column', sm: 'row' },
        gap: 2
      }}>
        <Box>
          <Typography variant="h4" sx={{ 
            color: 'primary.main', 
            fontWeight: 'bold',
            fontSize: { xs: '1.75rem', sm: '2rem' }
          }}>
            Dashboard Overview
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
            Welcome back! Here's what's happening with your business today.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Chip 
            icon={<CalendarToday />}
            label={new Date().toLocaleDateString('en-LK', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
            variant="outlined"
            sx={{ fontWeight: 500 }}
          />
          {/* ADD THIS LINE - Add the ClearCacheButton component */}
          <ClearCacheButton />
          {/* <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate('/sales/new')}
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            New Sale
          </Button> */}
        </Box>
      </Box>

      {/* Error Alert if exists but we have some data */}
      {error && (
        <Alert 
          severity="warning" 
          sx={{ 
            mb: 4,
            borderRadius: 2,
            '& .MuiAlert-icon': { alignItems: 'center' }
          }}
        >
          {error} (Showing available data)
        </Alert>
      )}

      {/* Quick Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 5 }}>
        {statCards.map((stat, index) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
            <Card 
              sx={{ 
                height: '100%',
                borderRadius: 3,
                boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                transition: 'transform 0.2s, box-shadow 0.2s',
                cursor: 'pointer',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.1)'
                }
              }}
              onClick={() => navigate(stat.link)}
            >
              <CardContent sx={{ p: 3 }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar
                    sx={{
                      backgroundColor: stat.bgColor,
                      color: stat.color,
                      width: 56,
                      height: 56
                    }}
                  >
                    {React.cloneElement(stat.icon, { fontSize: 'large' })}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      {stat.title}
                    </Typography>
                    <Typography variant="h5" sx={{ 
                      fontWeight: 'bold',
                      fontSize: { xs: '1.5rem', sm: '1.75rem' }
                    }}>
                      {stat.value}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Main Content Grid */}
      <Grid container spacing={3}>
        {/* Left Column - Inventory Status */}
        <Grid item xs={12} lg={8}>
          <Grid container spacing={3}>
            {/* Recent Products */}
            <Grid item xs={12}>
              <Paper 
                sx={{ 
                  borderRadius: 3,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                  overflow: 'hidden'
                }}
              >
                <Box sx={{ 
                  p: 3, 
                  backgroundColor: 'primary.50',
                  borderBottom: '1px solid',
                  borderColor: 'divider'
                }}>
                  <Stack 
                    direction={{ xs: 'column', sm: 'row' }} 
                    justifyContent="space-between" 
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    spacing={2}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      Recent Products
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => navigate('/products')}
                      sx={{ borderRadius: 2 }}
                    >
                      View All Products
                    </Button>
                  </Stack>
                </Box>
                <Box sx={{ p: { xs: 2, sm: 3 } }}>
                  {Array.isArray(products) && products.length > 0 ? (
                    <TableContainer>
                      <Table size="medium">
                        <TableHead>
                          <TableRow>
                            <TableCell><strong>Product</strong></TableCell>
                            <TableCell><strong>Category</strong></TableCell>
                            <TableCell><strong>Price</strong></TableCell>
                            <TableCell><strong>Stock</strong></TableCell>
                            <TableCell><strong>Status</strong></TableCell>
                            <TableCell><strong>Actions</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {products.slice(0, 5).map((product) => (
                            <TableRow 
                              key={product.id} 
                              hover 
                              sx={{ 
                                '&:last-child td, &:last-child th': { border: 0 },
                                '&:hover': { backgroundColor: 'action.hover' }
                              }}
                            >
                              <TableCell>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                  {product.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {product.product_code}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip 
                                  label={product.category} 
                                  size="small" 
                                  variant="outlined"
                                  sx={{ borderRadius: 1 }}
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600 }}>
                                  {product.price_lkr || formatCurrency(product.price)}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Stack spacing={0.5}>
                                  <Typography variant="body2">
                                    {product.quantity} {product.unit}
                                  </Typography>
                                  {product.quantity <= (product.reorder_level || 0) && (
                                    <Chip 
                                      label="Low Stock" 
                                      size="small" 
                                      color="warning"
                                      sx={{ width: 'fit-content' }}
                                    />
                                  )}
                                </Stack>
                              </TableCell>
                              <TableCell>
                                <Chip 
                                  label={product.is_active ? 'Active' : 'Inactive'} 
                                  color={product.is_active ? 'success' : 'error'}
                                  size="small"
                                  sx={{ borderRadius: 1 }}
                                />
                              </TableCell>
                              <TableCell>
                                <Tooltip title="View Product">
                                  <IconButton 
                                    size="small" 
                                    onClick={() => handleViewProduct(product.id)}
                                    sx={{ 
                                      color: 'primary.main',
                                      '&:hover': { backgroundColor: 'primary.50' }
                                    }}
                                  >
                                    <Visibility />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Box sx={{ 
                      p: 4, 
                      textAlign: 'center',
                      backgroundColor: 'grey.50',
                      borderRadius: 2
                    }}>
                      <Inventory sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                      <Typography color="text.secondary" variant="body1">
                        No products found
                      </Typography>
                      <Button 
                        variant="text" 
                        onClick={() => navigate('/products/new')}
                        sx={{ mt: 2 }}
                      >
                        Add your first product
                      </Button>
                    </Box>
                  )}
                </Box>
              </Paper>
            </Grid>

            {/* Recent Customers */}
            <Grid item xs={12}>
              <Paper 
                sx={{ 
                  borderRadius: 3,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                  overflow: 'hidden'
                }}
              >
                <Box sx={{ 
                  p: 3, 
                  backgroundColor: 'success.50',
                  borderBottom: '1px solid',
                  borderColor: 'divider'
                }}>
                  <Stack 
                    direction={{ xs: 'column', sm: 'row' }} 
                    justifyContent="space-between" 
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    spacing={2}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      Recent Customers
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => navigate('/customers')}
                      sx={{ borderRadius: 2 }}
                    >
                      View All Customers
                    </Button>
                  </Stack>
                </Box>
                <Box sx={{ p: { xs: 2, sm: 3 } }}>
                  {Array.isArray(customers) && customers.length > 0 ? (
                    <TableContainer>
                      <Table size="medium">
                        <TableHead>
                          <TableRow>
                            <TableCell><strong>Customer</strong></TableCell>
                            <TableCell><strong>Type</strong></TableCell>
                            <TableCell><strong>Contact</strong></TableCell>
                            <TableCell><strong>Credit Limit</strong></TableCell>
                            <TableCell><strong>Balance</strong></TableCell>
                            <TableCell><strong>Actions</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {customers.slice(0, 5).map((customer) => (
                            <TableRow 
                              key={customer.id} 
                              hover 
                              sx={{ 
                                '&:last-child td, &:last-child th': { border: 0 },
                                '&:hover': { backgroundColor: 'action.hover' }
                              }}
                            >
                              <TableCell>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                  {customer.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {customer.customer_code}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip 
                                  label={customer.customer_type} 
                                  size="small"
                                  sx={{ 
                                    backgroundColor: customer.customer_type === 'corporate' ? 'primary.50' : 'secondary.50',
                                    color: customer.customer_type === 'corporate' ? 'primary.main' : 'secondary.main',
                                    borderRadius: 1
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                <Stack spacing={0.5}>
                                  <Typography variant="body2">{customer.email}</Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {customer.phone}
                                  </Typography>
                                </Stack>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {customer.credit_limit_lkr || formatCurrency(customer.credit_limit)}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography 
                                  variant="subtitle2" 
                                  sx={{ 
                                    color: customer.outstanding_balance === 0 ? 'success.main' : 'error.main',
                                    fontWeight: 600
                                  }}
                                >
                                  {customer.outstanding_balance_lkr || formatCurrency(customer.outstanding_balance)}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Tooltip title="View Customer">
                                  <IconButton 
                                    size="small" 
                                    onClick={() => handleViewCustomer(customer.id)}
                                    sx={{ 
                                      color: 'primary.main',
                                      '&:hover': { backgroundColor: 'primary.50' }
                                    }}
                                  >
                                    <Visibility />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Box sx={{ 
                      p: 4, 
                      textAlign: 'center',
                      backgroundColor: 'grey.50',
                      borderRadius: 2
                    }}>
                      <People sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                      <Typography color="text.secondary" variant="body1">
                        No customers found
                      </Typography>
                      <Button 
                        variant="text" 
                        onClick={() => navigate('/customers/new')}
                        sx={{ mt: 2 }}
                      >
                        Add your first customer
                      </Button>
                    </Box>
                  )}
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Grid>

        {/* Right Column - Quick Stats & Modules */}
       <Grid item xs={12} lg={4}>
          <Stack spacing={3}>
            {/* Quick Actions */}
            <Paper sx={{ 
              borderRadius: 3,
              boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
              p: 3
            }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
                Quick Actions
              </Typography>
              <Grid container spacing={2}>
                {[
                 // { label: 'New Sale', icon: <ShoppingCart />, color: 'primary', path: '/sales/new' },
                 // { label: 'New Invoice', icon: <Receipt />, color: 'success', path: '/invoices/new' },
                 // { label: 'Add Product', icon: <Inventory />, color: 'warning', path: '/products/new' },
                 // { label: 'Add Customer', icon: <People />, color: 'info', path: '/customers/new' },
                 // { label: 'New Quotation', icon: <Description />, color: 'secondary', path: '/quotations/new' },
                 // { label: 'View Reports', icon: <TrendingUp />, color: 'error', path: '/reports' }
                ].map((action, index) => (
                  <Grid item xs={6} key={index}>
                    <Card 
                      sx={{ 
                        borderRadius: 2,
                        cursor: 'pointer',
                        transition: 'transform 0.2s, box-shadow 0.2s',
                        '&:hover': { 
                          transform: 'translateY(-2px)',
                          boxShadow: 3
                        }
                      }}
                      onClick={() => navigate(action.path)}
                    >
                      <CardContent sx={{ 
                        p: 2, 
                        textAlign: 'center',
                        '&:last-child': { pb: 2 }
                      }}>
                        <Box sx={{ 
                          color: `${action.color}.main`,
                          mb: 1,
                          display: 'flex',
                          justifyContent: 'center'
                        }}>
                          {React.cloneElement(action.icon, { fontSize: 'large' })}
                        </Box>
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                          {action.label}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Paper>  

            {/* System Status */}
            <Paper sx={{ 
              borderRadius: 3,
              boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
              p: 3
            }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
                System Status
              </Typography>
              <Stack spacing={2}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2">Database Connection</Typography>
                  <Chip label="Connected" color="success" size="small" sx={{ borderRadius: 1 }} />
                </Stack>
                <Divider />
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2">API Server</Typography>
                  <Chip label="Online" color="success" size="small" sx={{ borderRadius: 1 }} />
                </Stack>
                <Divider />
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2">Last Data Sync</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 500 }}>
                    {new Date().toLocaleTimeString('en-LK')}
                  </Typography>
                </Stack>
                <Divider />
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2">Active Users</Typography>
                  <Chip label="1 Online" size="small" sx={{ borderRadius: 1 }} />
                </Stack>
              </Stack>
            </Paper>

            {/* Low Stock Alert */}
            {lowStockProducts > 0 && (
              <Paper sx={{ 
                borderRadius: 3,
                boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                p: 3,
                backgroundColor: 'warning.50',
                border: '1px solid',
                borderColor: 'warning.200'
              }}>
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                  <Warning sx={{ color: 'warning.main' }} />
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'warning.800' }}>
                    Low Stock Alert
                  </Typography>
                </Stack>
                <Typography variant="body2" color="warning.800" sx={{ mb: 2 }}>
                  {lowStockProducts} product(s) are below reorder level and need attention.
                </Typography>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="caption" color="warning.700">
                    Action Required
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    color="warning"
                    onClick={() => navigate('/inventory')}
                    sx={{ borderRadius: 2 }}
                  >
                    View Items
                  </Button>
                </Stack>
              </Paper>
            )}

            {/* Outstanding Payments */}
            {totalOutstanding > 0 && (
              <Paper sx={{ 
                borderRadius: 3,
                boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                p: 3,
                backgroundColor: 'error.50',
                border: '1px solid',
                borderColor: 'error.200'
              }}>
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                  <AttachMoney sx={{ color: 'error.main' }} />
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'error.800' }}>
                    Outstanding Payments
                  </Typography>
                </Stack>
                <Typography variant="h5" sx={{ mb: 1, fontWeight: 'bold', color: 'error.900' }}>
                  {formatCurrency(totalOutstanding)}
                </Typography>
                <Typography variant="body2" color="error.800" sx={{ mb: 2 }}>
                  Total amount due from {Array.isArray(customers) ? customers.length : 0} customer(s)
                </Typography>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="caption" color="error.700">
                    Collection Required
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    color="error"
                    onClick={() => navigate('/invoices')}
                    sx={{ borderRadius: 2 }}
                  >
                    View Invoices
                  </Button>
                </Stack>
              </Paper>
            )}
          </Stack>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Dashboard;